// フロントエンド環境変数管理構造体
// サーバー側のEnvironmentInfoと統合された環境変数管理

// 環境情報のキャッシュ（同期返却用）
let cachedEnvInfo: EnvironmentInfo | null = null;

// 認証状態を追跡（API通信ログ制御用）
let isAuthenticated = false;

/**
 * 環境タイプ（サーバー側と統一）
 */
export type Environment =
  | "local"
  | "development"
  | "experiment"
  | "qa"
  | "staging"
  | "production";

/**
 * ログレベル（サーバー側と統一）
 */
export type LogLevel = "debug" | "info" | "warning" | "error" | "critical";

/**
 * ログサービスタイプ
 */
export type LogServiceType = "none" | "gcp" | "aws" | "local";

/**
 * GCP設定（サーバー側と統一）
 */
export interface GCPConfig {
  projectID: string;
  projectNumber: string;
  oauthClientID: string;
  organizationID: string;
}

/**
 * フロントエンド専用設定（実際にフロントエンドで使用される設定）
 */
export interface FrontendConfig {
  // 認証設定（フロントエンドで使用）
  skipAuth: boolean;
}

/**
 * 統合環境情報（フロントエンド用に最適化）
 */
export interface EnvironmentInfo {
  // 基本情報
  type: Environment;
  envName: string;
  version: string;

  // GCP設定（フロントエンドで使用）
  gcpConfig: GCPConfig;

  // フロントエンド専用設定
  frontend: FrontendConfig;
}

/**
 * 環境タイプを取得（サーバー側と統一）
 */
const getEnvironmentType = (): Environment => {
  const envName = (import.meta.env.VITE_ENVIRONMENT || "local").toLowerCase();
  switch (envName) {
    case "production":
    case "prod":
    case "prd":
      return "production";
    case "staging":
    case "stage":
    case "stg":
      return "staging";
    case "qa":
      return "qa";
    case "experiment":
      return "experiment";
    case "development":
    case "dev":
      return "development";
    default:
      return "local";
  }
};

/**
 * 環境変数から統合環境情報を取得
 */
export const initEnvironmentInfo = async (): Promise<void> => {
  // すでに初期化済みなら何もしない
  if (cachedEnvInfo) return;

  const environment = getEnvironmentType();
  const envName = import.meta.env.VITE_ENVIRONMENT || "local";
  const version = import.meta.env.VITE_APP_VERSION || "1.0.0";

  // GCP設定（環境変数から取得）
  const gcpConfig: GCPConfig = {
    projectID: import.meta.env.VITE_GCP_PROJECT_ID || "",
    projectNumber: import.meta.env.VITE_GCP_PROJECT_NUMBER || "",
    oauthClientID: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID || "",
    organizationID: import.meta.env.VITE_GCP_ORGANIZATION_ID || "",
  };

  // フロントエンド専用設定（環境変数から取得）
  const frontend: FrontendConfig = {
    skipAuth: import.meta.env.VITE_SKIP_AUTH === "true",
  };

  cachedEnvInfo = {
    type: environment,
    envName,
    version,
    gcpConfig,
    frontend,
  };
};

export const getEnvironmentInfo = (): EnvironmentInfo => {
  // キャッシュされている場合はそれを返す
  if (cachedEnvInfo) {
    return cachedEnvInfo;
  }

  // 初期化されていない場合は、最低限の同期情報を返す
  const envName = import.meta.env.VITE_ENVIRONMENT || "local";
  const version = import.meta.env.VITE_APP_VERSION || "1.0.0";
  const environment = getEnvironmentType();

  const tempEnvInfo: EnvironmentInfo = {
    type: environment,
    envName,
    version,
    gcpConfig: {
      projectID: import.meta.env.VITE_GCP_PROJECT_ID || "",
      projectNumber: import.meta.env.VITE_GCP_PROJECT_NUMBER || "",
      oauthClientID: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID || "",
      organizationID: import.meta.env.VITE_GCP_ORGANIZATION_ID || "",
    },
    frontend: {
      skipAuth: import.meta.env.VITE_SKIP_AUTH === "true",
    },
  };
  return tempEnvInfo;
};

/**
 * 環境判定ヘルパー関数（サーバー側と統一）
 */
export const isLocal = (): boolean => {
  const envInfo = getEnvironmentInfo();
  return envInfo.type === "local";
};

export const isProduction = (): boolean => {
  const envInfo = getEnvironmentInfo();
  return envInfo.type === "production";
};

export const isStaging = (): boolean => {
  const envInfo = getEnvironmentInfo();
  return envInfo.type === "staging";
};

export const isDevelopment = (): boolean => {
  const envInfo = getEnvironmentInfo();
  return envInfo.type === "development";
};

export const shouldSkipAuth = (): boolean => {
  const envInfo = getEnvironmentInfo();
  return envInfo.frontend.skipAuth;
};

/**
 * ベースURL取得関数（環境情報に基づく）
 * Connect-ESサーバー用のURLを返す。
 *
 * Connect-ES のパスは `/service.AdminService/LoginAdmin` のように
 * `/service.` プレフィックスで始まる。Nginx 側でその location を
 * Go サーバーにプロキシしているので、baseUrl は origin のままで OK。
 * （以前ここで `${origin}/api` を返していたが、Go サーバーに
 *   `/api/service.AdminService/LoginAdmin` のルートが無く 404 になっていた。）
 */
export const getBaseUrl = (): string => {
  return window.location.origin;
};

/**
 * デバッグ情報を取得
 */
export const getEnvironmentDebugInfo = (env: EnvironmentInfo) => {
  return {
    environment: env.type,
    envName: env.envName,
    version: env.version,
    isProduction: isProduction(),
    isStaging: isStaging(),
    isLocal: isLocal(),
    isDevelopment: isDevelopment(),
    googleOAuthClientId: env.gcpConfig.oauthClientID,
  };
};

/**
 * 認証状態を設定（API通信ログ制御用）
 */
export const setAuthenticationStatus = (authenticated: boolean) => {
  isAuthenticated = authenticated;
};

/**
 * 認証状態を取得
 */
export const getAuthenticationStatus = (): boolean => {
  return isAuthenticated;
};

/**
 * デバッグログを表示すべきかを判定。
 * デプロイ環境（fly.io 上の development / staging / production）では
 * Args/Reply の中身を Console に出さない。localhost / Vite dev のみログを出す。
 */
export const shouldShowDebugLogs = (): boolean => {
  return isLocal();
};
